export type BookingPurchaseDialogVariant =
  | 'book-event'
  | 'book-appointment'
  | 'buy-membership';

type ModalOptions = {
  controller: string;
  controllerAs: 'vm';
  inputs: Record<string, unknown>;
  template: string;
};

type Modal = {
  element: { modal: (action?: string) => void };
  close: Promise<unknown>;
};

type ModalDependencies = {
  showModal: (options: ModalOptions) => Promise<Modal>;
  logError?: (error: unknown) => void;
};

const VARIANT_CONFIG = {
  'book-event': {
    component: 'BookEventDialog',
    controller: 'BookingPurchaseDialogModalController',
  },
  'book-appointment': {
    component: 'BookAppointmentDialog',
    controller: 'BookingPurchaseDialogModalController',
  },
  'buy-membership': {
    component: 'BuyMembershipDialog',
    controller: 'BuyMembershipDialogModalController',
    dataBinding: 'vm.buyMembershipDialogData',
  },
} as const;

function reactDialogTemplate(componentName: string, dataBinding = 'vm.dialogData') {
  return [
    '<div class="modal fade modal-bookbuy" id="book-event-modal" tabindex="-1" role="dialog" aria-labelledby="book-event-modal-label">',
    '\t<div class="modal-overlay" aria-label="Close"></div>',
    '\t<div class="modal-dialog-small" role="document">',
    '\t\t<react-directive',
    `\t\t\tng-if="${dataBinding} !== undefined"`,
    `\t\t\tcomponent="'${componentName}'"`,
    `\t\t\tdata="${dataBinding}"`,
    '\t\t\trefresh-on-change="true">',
    '\t\t</react-directive>',
    '\t</div>',
    '</div>',
  ].join('\n');
}

export function createBookingPurchaseDialogModalService(
  dependencies: ModalDependencies
) {
  function open(options: Record<string, any> = {}) {
    const config =
      VARIANT_CONFIG[options.variant as BookingPurchaseDialogVariant];
    if (!config) {
      return undefined;
    }

    const inputs: Record<string, unknown> = {};
    ['event', 'membership', 'membershipsTrial'].forEach((key) => {
      if (options[key] !== undefined) {
        inputs[key] = options[key];
      }
    });
    if (options.variant !== 'buy-membership') {
      inputs.variant = options.variant;
    }

    return dependencies
      .showModal({
        controller: config.controller,
        controllerAs: 'vm',
        inputs,
        template: reactDialogTemplate(
          config.component,
          'dataBinding' in config ? config.dataBinding : undefined
        ),
      })
      .then((modal) => {
        modal.element.modal();
        modal.close.then(
          typeof options.onClose === 'function' ? options.onClose : () => undefined
        );
        return modal;
      })
      .catch((error) => {
        dependencies.logError?.(error);
        return undefined;
      });
  }

  return { open };
}

export function createBookingDialogData(args: {
  variant: 'book-event' | 'book-appointment';
  event: unknown;
  scope: unknown;
  hide: () => void;
  close: (result: boolean, delay: number) => void;
  createBookEvent: (
    scope: unknown,
    event: unknown,
    dismiss: () => void
  ) => unknown;
  createAppointment: (
    scope: unknown,
    event: unknown,
    dismiss: () => void
  ) => unknown;
}) {
  const dismiss = () => {
    args.hide();
    args.close(true, 200);
  };
  const create =
    args.variant === 'book-appointment'
      ? args.createAppointment
      : args.createBookEvent;
  return create(args.scope, args.event, dismiss);
}

export function createBuyMembershipDialogData(args: {
  scope: unknown;
  membership: unknown;
  membershipsTrial: unknown[];
  hide: () => void;
  close: (result: boolean, delay: number) => void;
  create: (
    scope: unknown,
    membership: unknown,
    membershipsTrial: unknown[],
    dismiss: () => void
  ) => unknown;
}) {
  const dismiss = () => {
    args.hide();
    args.close(true, 200);
  };
  return args.create(
    args.scope,
    args.membership,
    args.membershipsTrial,
    dismiss
  );
}
